import { MapPin, Sprout, ArrowUpRight, Compass, Heart, Phone, Mail, Shield } from 'lucide-react';

interface FooterProps {
  onShowModal: (type: 'contact' | 'directions' | 'privacy') => void;
}

export default function Footer({ onShowModal }: FooterProps) {
  return (
    <footer
      id="app-footer"
      className="bg-[#0E2017] text-[#EDE8DE] border-t border-emerald-900/40 relative overflow-hidden"
    >
      {/* Decorative Botanical Ambient Glow */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-emerald-700/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-80 h-80 bg-amber-600/5 rounded-full blur-3xl pointer-events-none" />

      {/* Top Heritage Invitation Banner */}
      <div className="border-b border-white/10 px-6 md:px-12 py-12 md:py-16 relative z-10">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-2 font-sans text-xs font-semibold uppercase tracking-widest text-amber-400 mb-3">
              <Sprout size={14} />
              Private Family Estate
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-white font-medium leading-tight">
              A quiet corner of the earth where time slows down.
            </h2>
            <p className="font-sans text-stone-300 text-sm mt-3 leading-relaxed">
              Preserved with love in Sirudavoor, Chengalpattu. Dedicated to family harmony, natural harvests, and stories told across generations.
            </p>
          </div>

          {/* Location & Coordinates Interactive Badge */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <a
              href="https://maps.app.goo.gl/VCtYoxuA4cLNtSZHA"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/40 transition-all duration-300 shadow-lg"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center flex-shrink-0 text-white shadow-md">
                <Compass size={22} className="group-hover:rotate-45 transition-transform duration-500 text-emerald-200" />
              </div>
              <div>
                <span className="font-serif font-medium text-white text-base block group-hover:text-amber-300 transition-colors">
                  Sirudavoor, Chengalpattu
                </span>
                <span className="font-sans text-xs text-stone-400 block tracking-wide">
                  12.6819° N, 79.9762° E · Tamil Nadu
                </span>
              </div>
              <ArrowUpRight size={18} className="text-stone-400 group-hover:text-amber-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all ml-2" />
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer Links & Info Grid */}
      <div className="px-6 md:px-12 py-12 md:py-16 relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">

          {/* Column 1: Brand & Philosophy */}
          <div className="md:col-span-2 max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-emerald-800/80 flex items-center justify-center text-emerald-200 border border-emerald-600/30">
                <Sprout size={18} />
              </div>
              <span className="font-serif text-xl font-bold text-white tracking-tight">
                Mithran Farm House
              </span>
            </div>
            <p className="font-sans text-sm text-stone-400 leading-relaxed mb-6">
              A private family retreat surrounded by native flora, stone verandas, and open skies. This digital sanctuary preserves our shared memories, seasonal events, and love for nature.
            </p>
            <div className="inline-flex items-center gap-2 text-xs font-medium text-emerald-300 bg-emerald-950/60 border border-emerald-800/60 px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Family Sanctuary · Closed to Public Commercial Bookings
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="font-sans text-xs font-bold uppercase tracking-widest text-white/90 mb-4">
              Sanctuary
            </h3>
            <ul className="space-y-2.5 text-sm text-stone-400">
              <li>
                <a href="#hero" className="hover:text-amber-300 transition-colors">
                  Home Sanctuary
                </a>
              </li>
              <li>
                <button
                  onClick={() => onShowModal('directions')}
                  className="hover:text-amber-300 transition-colors text-left cursor-pointer"
                >
                  Estate Map & Grounds
                </button>
              </li>
              <li>
                <button
                  onClick={() => onShowModal('contact')}
                  className="hover:text-amber-300 transition-colors text-left cursor-pointer"
                >
                  Family Concierge
                </button>
              </li>
              <li>
                <button
                  onClick={() => onShowModal('privacy')}
                  className="hover:text-amber-300 transition-colors text-left cursor-pointer"
                >
                  Privacy & Family Code
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: Estate Inquiries */}
          <div>
            <h3 className="font-sans text-xs font-bold uppercase tracking-widest text-white/90 mb-4">
              Information
            </h3>
            <div className="space-y-3 text-sm text-stone-400">
              <button
                onClick={() => onShowModal('directions')}
                className="flex items-center gap-2 text-left hover:text-amber-300 transition-colors cursor-pointer"
              >
                <MapPin size={15} className="text-emerald-400 flex-shrink-0" />
                <span>Sirudavoor, 603105</span>
              </button>
              <button
                onClick={() => onShowModal('contact')}
                className="flex items-center gap-2 text-left hover:text-amber-300 transition-colors cursor-pointer"
              >
                <Mail size={15} className="text-amber-400 flex-shrink-0" />
                <span>Family Contact Desk</span>
              </button>
              <button
                onClick={() => onShowModal('privacy')}
                className="flex items-center gap-2 text-left hover:text-amber-300 transition-colors cursor-pointer"
              >
                <Shield size={15} className="text-emerald-400 flex-shrink-0" />
                <span>Family Estate Privacy</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Copyright & Local Sign-off */}
      <div className="border-t border-white/10 px-6 md:px-12 py-6 relative z-10 bg-black/20">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-stone-400">
          <div className="flex items-center gap-2">
            <span>© {new Date().getFullYear()} Mithran Farm House Family.</span>
            <span>All memories and moments preserved with</span>
            <Heart size={12} className="text-red-400 inline" fill="currentColor" />
          </div>
          <div className="font-sans text-[11px] text-stone-500 tracking-wide uppercase">
            Sirudavoor · Chengalpattu District · Tamil Nadu, India
          </div>
        </div>
      </div>
    </footer>
  );
}
